import React from "react";
import { Link } from "react-router-dom";
import addupLogo from "./addup_logo.png";
import shotKo1 from "./screenshots/addup-ko-1.png";
import shotKo2 from "./screenshots/addup-ko-2.png";
import shotKo3 from "./screenshots/addup-ko-3.png";
import shotEn1 from "./screenshots/addup-en-1.png";
import shotEn2 from "./screenshots/addup-en-2.png";
import shotEn3 from "./screenshots/addup-en-3.png";
import { trackStoreClick } from "./firebaseAnalytics";

const APP_STORE_URL = "https://apps.apple.com/app/id6776875043";

type Locale = "ko" | "en";

const LOCALE_STORAGE_KEY = "addup-landing-locale";

const localeLabels: Record<Locale, string> = {
  ko: "KO",
  en: "EN",
};

const shots: Record<Locale, string[]> = {
  ko: [shotKo1, shotKo2, shotKo3],
  en: [shotEn1, shotEn2, shotEn3],
};

const content: Record<
  Locale,
  {
    navTerms: string;
    navPrivacy: string;
    overline: string;
    title: [string, string];
    lead: string;
    platformNote: string;
    appStore: string;
    philosophyLabel: string;
    philosophy: Array<{ eyebrow: string; title: string; body: string }>;
    featuresLabel: string;
    features: Array<[string, string]>;
    pricingLabel: string;
    pricingTitle: string;
    pricingDesc: string;
    freeTitle: string;
    freeItems: string[];
    proTitle: string;
    proItems: string[];
    proPrice: string;
    footerTerms: string;
    footerPrivacy: string;
  }
> = {
  ko: {
    navTerms: "이용약관",
    navPrivacy: "개인정보 처리방침",
    overline: "iOS · Apple Watch · 위젯 · 9개 언어",
    title: ["큰 목표도,", "한 번에 한 단계씩."],
    lead:
      "3초 만에 할 일을 적고, 목표는 단계별 로드맵으로 쪼개고, 완료의 순간을 함께 축하하는 할 일·루틴 앱입니다. 로그인 없이 바로 시작하고, iCloud로 조용히 동기화됩니다.",
    platformNote: "iOS 17.6+ · Apple Watch · 홈/잠금화면 위젯 · 로그인 없음, iCloud 동기화",
    appStore: "App Store에서 보기",
    philosophyLabel: "왜 Add Up인가요",
    philosophy: [
      {
        eyebrow: "Zero friction",
        title: "마찰 제로 입력",
        body: "앱을 열면 바로 입력창이 기다립니다. 생각나면 3초 안에 등록하고, 계정이나 설정을 강요하지 않습니다.",
      },
      {
        eyebrow: "Roadmap",
        title: "목표를 잠금 해제하듯",
        body: "큰 목표를 작은 단계로 쪼개고, 이전 단계를 끝내야 다음이 열립니다. 알림도 현재 단계만 — \"다 해야 해\"가 아니라 \"이것만 하면 돼\".",
      },
      {
        eyebrow: "Celebration",
        title: "완료를 축하하는 앱",
        body: "체크할 때마다 작은 축하, 하루를 끝내면 전체 화면 축하. 기록이 쌓이면 씨앗에서 나무까지 5단계 성장 배지가 자랍니다.",
      },
      {
        eyebrow: "Timeline",
        title: "시간이 보이는 하루",
        body: "주간 타임라인에서 하루를 시간 흐름으로 보고, 카드를 드래그해 일정을 옮깁니다. 빈 시간도 한눈에 보여요.",
      },
    ],
    featuresLabel: "전체 기능",
    features: [
      ["오늘 (홈)", "남은 할 일 헤드라인 + 원탭 입력바, 리스트/타임라인 전환"],
      ["주간 타임라인", "시간축 위 카드, 드래그로 시간 이동(10분 스냅)"],
      ["보관함", "날짜 없는 아이디어를 던져두고 나중에 오늘로"],
      ["로드맵", "단계 배열 목표, 순차 잠금 해제, 진행률 + 간트 차트"],
      ["통계", "스트릭 + 성장 배지, 주간 링 · 월간 히트맵 달력"],
      ["위젯 6종", "홈·잠금화면에서 바로 확인하고 완료 토글"],
      ["Apple Watch", "손목에서 체크·추가, iCloud로 폰과 동기화"],
      ["알림", "일정 알림 · 저녁 완료 확인 · 아침 브리핑, 로드맵은 현재 단계만"],
    ],
    pricingLabel: "무료 vs Pro",
    pricingTitle: "핵심은 무료로, 더 깊게는 Pro로.",
    pricingDesc: "할 일·보관함·타임라인은 전부 무료입니다. 반복, 무제한 로드맵, 상세 통계, 테마가 필요할 때만 Pro를 선택하세요.",
    freeTitle: "무료",
    freeItems: ["할 일 · 보관함 · 타임라인 전부", "로드맵 1개 진행", "기본 알림", "스트릭 · 성장 배지"],
    proTitle: "Pro",
    proItems: ["로드맵 무제한", "반복 · 미리 알림", "상세 통계 (주간·월간)", "사진 첨부 · 테마 8종 + 커스텀"],
    proPrice: "월 $2.99 · 연 $16.99 (7일 무료 체험) · 한국 연간 ₩29,000",
    footerTerms: "이용약관",
    footerPrivacy: "개인정보 처리방침",
  },
  en: {
    navTerms: "Terms",
    navPrivacy: "Privacy Policy",
    overline: "iOS · Apple Watch · Widgets · 9 languages",
    title: ["Big goals,", "one unlocked step at a time."],
    lead:
      "A to-do and routine app where capturing a task takes three seconds, big goals become step-by-step roadmaps, and every completion is celebrated. Start without an account — quietly synced with iCloud.",
    platformNote: "iOS 17.6+ · Apple Watch · Home & Lock Screen widgets · No login, iCloud sync",
    appStore: "View on the App Store",
    philosophyLabel: "Why Add Up",
    philosophy: [
      {
        eyebrow: "Zero friction",
        title: "Zero-friction capture",
        body: "The input bar is right there when you open the app. Write it down in three seconds — no account, no setup demanded.",
      },
      {
        eyebrow: "Roadmap",
        title: "Unlock goals like stages",
        body: "Split a big goal into small steps that unlock in order. Notifications only nudge the current step — \"just this one\", never \"do it all\".",
      },
      {
        eyebrow: "Celebration",
        title: "An app that celebrates you",
        body: "A small cheer for every check-off, a full-screen celebration when the day is done, and a five-stage growth badge from seed to tree.",
      },
      {
        eyebrow: "Timeline",
        title: "A day you can see",
        body: "The weekly timeline lays your day out as a flow of time. Drag cards to reschedule, and see your free hours at a glance.",
      },
    ],
    featuresLabel: "Everything inside",
    features: [
      ["Today (Home)", "Remaining-tasks headline + one-tap input, list/timeline views"],
      ["Weekly timeline", "Cards on a time axis, drag to move with 10-minute snapping"],
      ["Inbox", "Park undated ideas, then schedule them later"],
      ["Roadmaps", "Goals as ordered steps with sequential unlock, progress + Gantt view"],
      ["Statistics", "Streaks and growth badges, weekly rings, monthly heatmap calendar"],
      ["6 widgets", "Check tasks right from the Home and Lock Screen"],
      ["Apple Watch", "Check and add from your wrist, synced via iCloud"],
      ["Notifications", "Schedule alerts, evening review, morning briefing — roadmaps ping only the current step"],
    ],
    pricingLabel: "Free vs Pro",
    pricingTitle: "The core is free. Go deeper with Pro.",
    pricingDesc: "To-dos, inbox, and the timeline are entirely free. Choose Pro when you want repeats, unlimited roadmaps, detailed statistics, and themes.",
    freeTitle: "Free",
    freeItems: ["All to-dos · inbox · timeline", "1 active roadmap", "Basic notifications", "Streaks · growth badges"],
    proTitle: "Pro",
    proItems: ["Unlimited roadmaps", "Repeats · pre-reminders", "Detailed weekly & monthly stats", "Photo attachments · 8 themes + custom"],
    proPrice: "$2.99 / month · $16.99 / year (7-day free trial)",
    footerTerms: "Terms",
    footerPrivacy: "Privacy Policy",
  },
};

const detectLocale = (): Locale => {
  if (typeof window === "undefined") {
    return "en";
  }

  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored === "ko" || stored === "en") {
    return stored;
  }

  const browserLocales = window.navigator.languages?.length
    ? window.navigator.languages
    : [window.navigator.language];

  for (const locale of browserLocales) {
    if (locale.toLowerCase().startsWith("ko")) return "ko";
  }

  return "en";
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #eef4fe 0%, #ffffff 34%)",
    color: "var(--ink)",
  },
  shell: {
    width: "100%",
    maxWidth: 1120,
    margin: "0 auto",
    padding: "28px 20px 64px",
    boxSizing: "border-box",
  },
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 40,
    flexWrap: "wrap",
  },
  navBrand: {
    color: "var(--blue-deep)",
    textDecoration: "none",
    fontSize: 14,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    fontWeight: 800,
  },
  navActions: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
  },
  navLinks: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    fontSize: 14,
  },
  navLink: {
    color: "var(--ink-2)",
    textDecoration: "none",
    fontWeight: 600,
  },
  localeSwitch: {
    display: "inline-flex",
    gap: 4,
    padding: 4,
    borderRadius: 999,
    background: "#fff",
    border: "1px solid var(--rule-soft)",
  },
  localeButton: {
    minWidth: 42,
    padding: "8px 10px",
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: "var(--ink-3)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  activeLocaleButton: {
    background: "var(--blue)",
    color: "#fff",
    boxShadow: "0 6px 16px rgba(75, 141, 248, 0.35)",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 36,
    alignItems: "center",
    padding: "8px 0 40px",
  },
  overline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 14px",
    borderRadius: 999,
    background: "rgba(75, 141, 248, 0.1)",
    border: "1px solid rgba(75, 141, 248, 0.18)",
    color: "var(--blue-deep)",
    fontSize: 12,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    fontWeight: 700,
  },
  appId: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    margin: "22px 0 16px",
  },
  appLogo: {
    width: 64,
    height: 64,
    borderRadius: 16,
    objectFit: "cover",
    boxShadow: "0 10px 24px rgba(31, 42, 68, 0.14)",
  },
  appName: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: -0.3,
  },
  appCategory: {
    margin: "3px 0 0",
    fontSize: 13,
    color: "var(--ink-3)",
  },
  title: {
    margin: "6px 0 16px",
    fontSize: "clamp(2.3rem, 6vw, 4.2rem)",
    lineHeight: 1.08,
    letterSpacing: "-0.03em",
    fontWeight: 800,
    color: "var(--ink)",
  },
  titleAccent: {
    color: "var(--blue)",
  },
  lead: {
    margin: "0 0 18px",
    fontSize: 16,
    lineHeight: 1.8,
    color: "var(--ink-2)",
    maxWidth: 560,
  },
  badgeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginTop: 24,
  },
  appStoreButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 50,
    padding: "14px 28px",
    borderRadius: 999,
    background: "var(--blue)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    textDecoration: "none",
    boxShadow: "0 14px 30px rgba(75, 141, 248, 0.35)",
  },
  note: {
    marginTop: 16,
    fontSize: 13,
    lineHeight: 1.7,
    color: "var(--ink-3)",
  },
  heroVisualWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  halo: {
    position: "absolute",
    width: 380,
    height: 380,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(75, 141, 248, 0.2) 0%, rgba(75, 141, 248, 0.05) 46%, rgba(75, 141, 248, 0) 74%)",
    filter: "blur(4px)",
  },
  heroShot: {
    position: "relative",
    width: "min(100%, 340px)",
    borderRadius: 28,
    border: "1px solid var(--rule-soft)",
    boxShadow: "0 28px 60px rgba(31, 42, 68, 0.18)",
  },
  sectionLabel: {
    margin: "56px 0 20px",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: "var(--blue-deep)",
  },
  philosophyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
  },
  philosophyCard: {
    background: "#fff",
    border: "1px solid var(--rule-soft)",
    borderRadius: 20,
    padding: "22px 22px 20px",
    boxShadow: "0 12px 30px rgba(31, 42, 68, 0.07)",
  },
  cardEyebrow: {
    margin: "0 0 10px",
    fontSize: 11.5,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "var(--blue)",
    fontWeight: 700,
  },
  cardTitle: {
    margin: "0 0 8px",
    fontSize: 17.5,
    fontWeight: 800,
    color: "var(--ink)",
  },
  cardText: {
    margin: 0,
    fontSize: 13.5,
    lineHeight: 1.7,
    color: "var(--ink-2)",
  },
  shotsRow: {
    display: "flex",
    gap: 18,
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: 8,
  },
  shotImg: {
    width: "min(100%, 260px)",
    borderRadius: 22,
    border: "1px solid var(--rule-soft)",
    boxShadow: "0 20px 44px rgba(31, 42, 68, 0.14)",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 12,
  },
  featureCard: {
    background: "var(--bg-soft)",
    border: "1px solid var(--rule-soft)",
    borderRadius: 16,
    padding: "16px 18px",
  },
  featureTitle: {
    margin: "0 0 6px",
    fontSize: 14.5,
    fontWeight: 800,
    color: "var(--ink)",
  },
  featureBody: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.65,
    color: "var(--ink-2)",
  },
  pricingIntro: {
    maxWidth: 640,
  },
  pricingTitle: {
    margin: "0 0 10px",
    fontSize: "clamp(1.5rem, 3vw, 2.1rem)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
  },
  pricingDesc: {
    margin: 0,
    fontSize: 14.5,
    lineHeight: 1.75,
    color: "var(--ink-2)",
  },
  planGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 16,
    marginTop: 24,
  },
  planCard: {
    background: "#fff",
    border: "1px solid var(--rule-soft)",
    borderRadius: 20,
    padding: "24px 24px 22px",
  },
  planCardPro: {
    border: "1.5px solid rgba(75, 141, 248, 0.5)",
    boxShadow: "0 18px 40px rgba(75, 141, 248, 0.14)",
  },
  planName: {
    margin: "0 0 14px",
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  planNamePro: {
    color: "var(--blue-deep)",
  },
  planList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "grid",
    gap: 9,
  },
  planItem: {
    fontSize: 13.5,
    lineHeight: 1.6,
    color: "var(--ink-2)",
    display: "flex",
    gap: 8,
  },
  planCheck: {
    color: "var(--blue)",
    fontWeight: 800,
  },
  planPrice: {
    margin: "16px 0 0",
    paddingTop: 14,
    borderTop: "1px solid var(--rule-soft)",
    fontSize: 13,
    fontWeight: 700,
    color: "var(--ink)",
  },
  footer: {
    marginTop: 64,
    paddingTop: 18,
    borderTop: "1px solid var(--rule-soft)",
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    color: "var(--ink-3)",
    fontSize: 13,
  },
};

const AddUpLanding: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  const handleStoreClick = React.useCallback(() => {
    trackStoreClick({
      app_name: "addup",
      store_name: "app_store",
      source_page: "addup_landing",
    });
  }, []);

  React.useEffect(() => {
    setLocale(detectLocale());
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
  }, [locale]);

  const copy = content[locale];
  const localeShots = shots[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <nav style={styles.nav}>
          <Link to="/" style={styles.navBrand}>
            Daydream Factory
          </Link>
          <div style={styles.navActions}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {(Object.keys(localeLabels) as Locale[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLocale(option)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === option ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[option]}
                </button>
              ))}
            </div>
            <div style={styles.navLinks}>
              <Link to="/addup/terms" style={styles.navLink}>
                {copy.navTerms}
              </Link>
              <Link to="/addup/privacy" style={styles.navLink}>
                {copy.navPrivacy}
              </Link>
            </div>
          </div>
        </nav>

        <section style={styles.hero}>
          <div>
            <span style={styles.overline}>{copy.overline}</span>

            <div style={styles.appId}>
              <img src={addupLogo} alt="Add Up app icon" style={styles.appLogo} />
              <div>
                <p style={styles.appName}>Add Up</p>
                <p style={styles.appCategory}>Productivity · To-do · Routine</p>
              </div>
            </div>

            <h1 style={styles.title}>
              {copy.title[0]}
              <br />
              <span style={styles.titleAccent}>{copy.title[1]}</span>
            </h1>
            <p style={styles.lead}>{copy.lead}</p>

            <div style={styles.badgeRow}>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                onClick={handleStoreClick}
                style={styles.appStoreButton}
              >
                ✦ {copy.appStore}
              </a>
            </div>

            <p style={styles.note}>{copy.platformNote}</p>
          </div>

          <div style={styles.heroVisualWrap}>
            <div style={styles.halo} />
            <img src={localeShots[0]} alt="Add Up today screen" style={styles.heroShot} />
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.philosophyLabel}</p>
          <div style={styles.philosophyGrid}>
            {copy.philosophy.map((card) => (
              <article key={card.title} style={styles.philosophyCard}>
                <p style={styles.cardEyebrow}>{card.eyebrow}</p>
                <h3 style={styles.cardTitle}>{card.title}</h3>
                <p style={styles.cardText}>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>Preview</p>
          <div style={styles.shotsRow}>
            {localeShots.map((src, index) => (
              <img key={src} src={src} alt={`Add Up screenshot ${index + 1}`} style={styles.shotImg} />
            ))}
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.featuresLabel}</p>
          <div style={styles.featureGrid}>
            {copy.features.map(([title, body]) => (
              <article key={title} style={styles.featureCard}>
                <h3 style={styles.featureTitle}>{title}</h3>
                <p style={styles.featureBody}>{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.pricingLabel}</p>
          <div style={styles.pricingIntro}>
            <h2 style={styles.pricingTitle}>{copy.pricingTitle}</h2>
            <p style={styles.pricingDesc}>{copy.pricingDesc}</p>
          </div>

          <div style={styles.planGrid}>
            <article style={styles.planCard}>
              <p style={styles.planName}>{copy.freeTitle}</p>
              <ul style={styles.planList}>
                {copy.freeItems.map((item) => (
                  <li key={item} style={styles.planItem}>
                    <span style={styles.planCheck}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </article>

            <article style={{ ...styles.planCard, ...styles.planCardPro }}>
              <p style={{ ...styles.planName, ...styles.planNamePro }}>{copy.proTitle}</p>
              <ul style={styles.planList}>
                {copy.proItems.map((item) => (
                  <li key={item} style={styles.planItem}>
                    <span style={styles.planCheck}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p style={styles.planPrice}>{copy.proPrice}</p>
            </article>
          </div>
        </section>

        <footer style={styles.footer}>
          <span>support@daydreamfactory.net</span>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Link to="/addup/terms" style={styles.navLink}>
              {copy.footerTerms}
            </Link>
            <Link to="/addup/privacy" style={styles.navLink}>
              {copy.footerPrivacy}
            </Link>
          </div>
        </footer>
      </div>
    </main>
  );
};

export default AddUpLanding;
